import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Breadcrumb, Layout } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import chunk from 'lodash.chunk';
import { useStore } from '../../../store';
import breadcrumbSeparator from '../../../assets/icons/breadcrumbSeparator.svg';
import { ReactComponent as FileText } from '../../../assets/icons/fileText.svg';
import { ReactComponent as Table } from '../../../assets/icons/table.svg';
import { ReactComponent as FilterBar } from '../../../assets/icons/filter_bar.svg';
import { useActions } from '../../providers/actions-provider/actions.context';
import { REPORTS_LIST_PATH } from '../../routing/paths.constants';
import PageTitle from '../../lib/page-title';
import BreadcrumbItem from '../../lib/breadcrumbItem';
import SecondaryButton from '../../lib/secondary-button';
import PrimaryButton from '../../lib/primary-button';
import DeleteModal from '../../lib/delete-modal';
import FirstTab from './first-tab.page';
import SecondTab from './second-tab.page';
import ThirdTab from './third-tab.page';
import checkPermission from '../../../utils/permissions/permissions';
import PermissionProvider from '../../providers/permissionProvider';
// import ThirdTab from './third-tab-refactor';

interface ICrumb {
  path: string;
  breadcrumbName: string;
}

const EditReport = (): JSX.Element => {
  const { executedReport, listsForReport } = useStore();
  const { reportActions, userActions } = useActions();
  const { push, replace, location: { pathname } } = useHistory();
  const positionOfExec = pathname.indexOf('exec-');
  const positionOfID = pathname.indexOf('/', positionOfExec + 5);
  const id = pathname
    .slice(positionOfExec + 5, positionOfID === -1 ? pathname.length : positionOfID);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const tab = pathname.slice(pathname.lastIndexOf('/') + 1);

  let dataForCrumbs: ICrumb[] = [];
  const initialCrumb = {
    path: REPORTS_LIST_PATH,
    breadcrumbName: 'Reports List',
  };
  if (pathname === REPORTS_LIST_PATH) {
    dataForCrumbs.push(initialCrumb);
  } else {
    // preparing routes for creation crumbs
    // without first three static '', 'reports-list', 'edit-report', 'exec-16'
    const prepareRoutes = pathname.split('/').slice(4);
    // break array by chunk of /name-of-nest and /id
    const chunks = chunk(prepareRoutes, 2);
    let path = REPORTS_LIST_PATH;
    const crumbsWithoutInitial = chunks.map((el) => {
      // each next path need to include previous
      path += `/${el[0]}/${el[1]}`;
      return {
        path,
        breadcrumbName: el[0],
      };
    });
    dataForCrumbs = [initialCrumb, ...crumbsWithoutInitial];
  }

  useEffect(() => {
    reportActions.getExecutedReport(+id);
    // TODO: we need to create independent data points for the filterFields
    // if (!listsForReport.loaded) {
    reportActions.getListsForReport(id);
    // }
  }, [id]);

  const toggleModal = () => {
    setModalVisible(!modalVisible);
  };

  const onConfirmDelete = () => {
    reportActions.deleteReport(+id);
    toggleModal();
    if (positionOfID === -1) {
      push(REPORTS_LIST_PATH);
      reportActions.getReportsList();
    } else {
      push(`${REPORTS_LIST_PATH}${pathname.slice(positionOfID)}`);
      reportActions.getReportsList();
    }
  };

  function switchTab() {
    let renderTab;
    const {
      regex,
      quickRanges,
      operators,
      filterFields,
    } = listsForReport.data;
    switch (pathname.slice(pathname.lastIndexOf('/') + 1)) {
      case 'base':
        renderTab = (
          <FirstTab />
        );
        break;
      case 'table':
        renderTab = (
          <SecondTab />
        );
        break;
      case 'filters':
        renderTab = (
          <ThirdTab
            regex={regex}
            quickRanges={quickRanges}
            operators={operators}
            filterFields={filterFields}
          />
        );
        break;
      default:
        break;
    }
    return (<PermissionProvider pageId="reports">{renderTab}</PermissionProvider>);
  }

  const onInfoClick = (url: string) => (
    () => {
      userActions.getHelp(url);
    }
  );

  return (
    <Layout className="main-container">
      <PermissionProvider pageId="reports">
        <Breadcrumb
          itemRender={BreadcrumbItem}
          routes={dataForCrumbs}
          separator={<img src={breadcrumbSeparator} alt="breadcrumb separator" />}
          params={{ dataForCrumbs }}
        />
        <PageTitle title={executedReport.data.name} back>
          <div style={{ display: 'flex' }}>
            <PrimaryButton
              title="Execute Report"
              className="left"
              onClick={() => {
                push(`${REPORTS_LIST_PATH}${pathname.slice(25, pathname.length - tab.length - 1)}`);
              }}
            />
            { checkPermission('reports', 'write') && <SecondaryButton title="Delete Report" onClick={toggleModal} />}
          </div>
        </PageTitle>
        <strong>{`${window.location.protocol}//${window.location.host}${executedReport.data.url}`}</strong>

        <div className="zones-steps">
          <button
            onClick={() => {
              replace(`${pathname.slice(0, pathname.lastIndexOf('/'))}/base`);
            }}
            type="button"
            className={`zones-steps__step ${tab === 'base' && 'step__active'}`}
          >
            <FileText className="zones-steps__icon zones-steps__icon--fill" />
            <span>
              Base Information
            </span>
          </button>
          <InfoCircleOutlined style={{ padding: '5px', fontSize: '20px' }} onClick={onInfoClick('/docs/User-Manual/reports/defining/#base-information')} />
          <button
            onClick={() => {
              replace(`${pathname.slice(0, pathname.lastIndexOf('/'))}/table`);
            }}
            type="button"
            className={`zones-steps__step ${tab === 'table' && 'step__active'}`}
          >
            <Table className="zones-steps__icon zones-steps__icon--fill" />
            <span>
              Report Table
            </span>
          </button>
          <InfoCircleOutlined style={{ padding: '5px', fontSize: '20px' }} onClick={onInfoClick('/docs/User-Manual/reports/defining/#report-table')} />
          <button
            onClick={() => {
              replace(`${pathname.slice(0, pathname.lastIndexOf('/'))}/filters`);
            }}
            type="button"
            className={`zones-steps__step ${tab === 'filters' && 'step__active'}`}
          >
            <FilterBar className="zones-steps__icon zones-steps__icon--fill" />
            <span>
              Filters
            </span>
          </button>
          <InfoCircleOutlined style={{ padding: '5px', fontSize: '20px' }} onClick={onInfoClick('/docs/User-Manual/reports/defining/#filters')} />
        </div>
        {switchTab()}
        <DeleteModal
          text={`${executedReport.data.name} report`}
          modalVisible={modalVisible}
          onConfirm={onConfirmDelete}
          onCancel={toggleModal}
        />
      </PermissionProvider>
    </Layout>
  );
};

EditReport.displayName = 'EditReport';
export default EditReport;
